import React, { useMemo, useState, useEffect, useCallback } from 'react';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import './CustomTree.scss';
import UtilityService from '../../services/UtillityServices/UtilityService';
import { alertService } from '../../services/AlertService';

const AREA_KEY_PREFIX = 'a-';
const CONT_KEY_PREFIX = 'c-';

const buildAreaKey = (idArea) => `${AREA_KEY_PREFIX}${idArea}`;
const buildContKey = (idContenuto) => `${CONT_KEY_PREFIX}${idContenuto}`;

const isAreaKey = (key) => typeof key === 'string' && key.startsWith(AREA_KEY_PREFIX);
const isContKey = (key) => typeof key === 'string' && key.startsWith(CONT_KEY_PREFIX);

/**
 * Costruisce il treeData nel formato richiesto da rc-tree a partire
 * dal nostro modello (aree con children + contenuti).
 *
 * Ordine di rendering: i nodi vengono restituiti già ordinati per il
 * campo `ordine` (con fallback su id) - il sort lo gestisce il backend,
 * qui ci limitiamo a mantenere l'ordine ricevuto.
 *
 * Ogni nodo include i metadati grezzi (`raw`) per poterli reinviare al
 * backend in fase di drop.
 */
const convertAreasToRcTreeData = (areas, renderTitle) => {
    const walk = (area) => {
        const areaKey = buildAreaKey(area.id_area);
        const children = [];

        (area.contenuti || []).forEach((c) => {
            children.push({
                key: buildContKey(c.id_contenuto),
                title: renderTitle({ type: 'cont', data: c }),
                isLeaf: true,
                nodeType: 'cont',
                raw: c,
            });
        });

        (area.children || []).forEach((sub) => {
            children.push(walk(sub));
        });

        return {
            key: areaKey,
            title: renderTitle({ type: 'area', data: area }),
            nodeType: 'area',
            raw: area,
            children,
        };
    };

    return (areas || []).map(walk);
};

/**
 * Verifica se `candidateKey` è un discendente di `nodeKey` nel treeData.
 * Usato per evitare cicli quando si trascina un'area dentro un suo
 * discendente.
 */
const isDescendant = (treeData, nodeKey, candidateKey) => {
    const findNode = (nodes, key) => {
        for (const n of nodes) {
            if (n.key === key) return n;
            if (n.children?.length) {
                const found = findNode(n.children, key);
                if (found) return found;
            }
        }
        return null;
    };
    const containsKey = (nodes, key) => {
        for (const n of nodes) {
            if (n.key === key) return true;
            if (n.children?.length && containsKey(n.children, key)) return true;
        }
        return false;
    };
    const node = findNode(treeData, nodeKey);
    if (!node || !node.children?.length) return false;
    return containsKey(node.children, candidateKey);
};

/**
 * Clona ricorsivamente il treeData escludendo i campi non serializzabili
 * (in particolare `title`, che e' un React element con riferimenti
 * ciclici interni). Manteniamo solo cio' che serve a `flattenForBackend`.
 */
const cloneTreeForBackend = (nodes) =>
    (nodes || []).map((n) => ({
        key: n.key,
        nodeType: n.nodeType,
        raw: n.raw,
        isLeaf: n.isLeaf,
        children: n.children ? cloneTreeForBackend(n.children) : undefined,
    }));

/**
 * Applica al treeData lo spostamento risultato del drag&drop.
 * Implementazione ispirata all'esempio standard di antd/rc-tree, ma
 * con clone manuale per evitare cicli da React element (title).
 */
const applyDrop = (treeData, info) => {
    const dropKey = info.node.key;
    const dragKey = info.dragNode.key;
    const dropPos = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

    const data = cloneTreeForBackend(treeData);

    const loop = (nodes, key, callback) => {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].key === key) {
                return callback(nodes[i], i, nodes);
            }
            if (nodes[i].children) {
                loop(nodes[i].children, key, callback);
            }
        }
    };

    let dragObj;
    loop(data, dragKey, (item, index, arr) => {
        arr.splice(index, 1);
        dragObj = item;
    });

    if (!info.dropToGap) {
        loop(data, dropKey, (item) => {
            item.children = item.children || [];
            item.children.unshift(dragObj);
        });
    } else {
        let ar = [];
        let i = 0;
        loop(data, dropKey, (_item, index, arr) => {
            ar = arr;
            i = index;
        });
        if (dropPosition === -1) {
            ar.splice(i, 0, dragObj);
        } else {
            ar.splice(i + 1, 0, dragObj);
        }
    }
    return data;
};

/**
 * Visita il treeData ricostruito e produce gli array piatti da inviare
 * al backend: ogni nodo riporta il nuovo parent e il nuovo `ordinamento`
 * (indice fra i suoi siblings, 0-based).
 *
 * Il nome del campo e' `ordinamento` per allinearsi alla colonna gia'
 * presente in produzione su `contenuti` (letta dall'area corsista).
 */
const flattenForBackend = (treeData) => {
    const aree = [];
    const contenuti = [];

    const walk = (nodes, parentAreaId) => {
        nodes.forEach((node, idx) => {
            if (node.nodeType === 'area') {
                aree.push({
                    id_area: node.raw.id_area,
                    id_area_padre: parentAreaId,
                    ordinamento: idx,
                });
                if (node.children?.length) {
                    walk(node.children, node.raw.id_area);
                }
            } else if (node.nodeType === 'cont') {
                contenuti.push({
                    id_contenuto: node.raw.id_contenuto,
                    id_area: parentAreaId,
                    ordinamento: idx,
                });
            }
        });
    };

    walk(treeData, 0);
    return { aree, contenuti };
};

const CustomTree = ({
    areas,
    setSelectedNodeTree,
    setModalStateAree,
    setInputAreaValue,
    setSelectedContenuto,
    idCorso,
    onReorder,
}) => {
    const [expandedKeys, setExpandedKeys] = useState(() => {
        const saved = localStorage.getItem('openFolders');
        if (!saved) return [];
        try {
            const parsed = JSON.parse(saved);
            return Array.isArray(parsed) ? parsed.map((id) => buildAreaKey(id)) : [];
        } catch {
            return [];
        }
    });

    const [selectedKeys, setSelectedKeys] = useState(() => {
        const cid = localStorage.getItem('selectedContenutoId');
        return cid ? [buildContKey(parseInt(cid, 10))] : [];
    });

    useEffect(() => {
        const ids = expandedKeys
            .filter(isAreaKey)
            .map((k) => parseInt(k.replace(AREA_KEY_PREFIX, ''), 10));
        localStorage.setItem('openFolders', JSON.stringify(ids));
    }, [expandedKeys]);

    const openModalEditArea = useCallback((area) => {
        setSelectedNodeTree(area);
        setInputAreaValue(area.titolo);
        setModalStateAree((state) => ({
            ...state,
            showModalUpdateAreaOrSottoarea: true,
        }));
    }, [setSelectedNodeTree, setInputAreaValue, setModalStateAree]);

    const renderTitle = useCallback(({ type, data }) => {
        if (type === 'area') {
            const hasChildren =
                (data.children && data.children.length > 0) ||
                (data.contenuti && data.contenuti.length > 0);
            return (
                <span className="custom-tree-node-title custom-tree-area" title={data.titolo}>
                    <span
                        className={`bi ${hasChildren ? 'bi-folder-fill' : 'bi-folder'} text-primary me-1`}
                    />
                    <span className="custom-tree-label">{data.titolo}</span>
                    <span
                        role="button"
                        tabIndex={-1}
                        className="bi bi-pencil edit-icon text-dark ms-1 small"
                        onClick={(e) => {
                            e.stopPropagation();
                            openModalEditArea(data);
                        }}
                    />
                </span>
            );
        }
        return (
            <span className="custom-tree-node-title custom-tree-cont" title={data.titolo}>
                <span className="me-1">{UtilityService.generateIconContenuti(data)}</span>
                <span className="custom-tree-label">{data.titolo}</span>
            </span>
        );
    }, [openModalEditArea]);

    const treeData = useMemo(
        () => convertAreasToRcTreeData(areas, renderTitle),
        [areas, renderTitle]
    );

    const onSelect = (keys, info) => {
        const node = info?.node;
        if (!node) return;
        if (node.nodeType === 'cont') {
            setSelectedContenuto(node.raw);
            try {
                localStorage.setItem('selectedContenutoId', node.raw.id_contenuto);
            } catch {}
            setSelectedKeys([node.key]);
        } else if (node.nodeType === 'area') {
            setExpandedKeys((prev) => {
                if (prev.includes(node.key)) {
                    return prev.filter((k) => k !== node.key);
                }
                return [...prev, node.key];
            });
            setSelectedKeys([]);
        }
    };

    const onExpand = (keys) => {
        setExpandedKeys(keys);
    };

    /**
     * Filtra i drop "non permessi" prima ancora che vengano applicati.
     * - Un contenuto non può finire a livello root (gli serve un'area).
     * - Un nodo non può essere spostato dentro un proprio discendente.
     * - Non si può rilasciare "dentro" un contenuto (sono foglie).
     */
    const allowDrop = ({ dragNode, dropNode, dropPosition }) => {
        if (!dragNode || !dropNode) return false;

        if (dropNode.nodeType === 'cont' && dropPosition === 0) {
            return false;
        }

        if (dragNode.nodeType === 'cont') {
            if (dropPosition !== 0) {
                const dropPos = (dropNode.pos || '').split('-');
                const isRootLevel = dropPos.length <= 2;
                if (isRootLevel) {
                    return false;
                }
            }
        }

        if (dragNode.nodeType === 'area') {
            if (isDescendant(treeData, dragNode.key, dropNode.key)) {
                return false;
            }
        }

        return true;
    };

    const onDrop = async (info) => {
        try {
            if (!info?.dragNode || !info?.node) return;

            if (info.dragNode.key === info.node.key) {
                return;
            }

            const newTreeData = applyDrop(treeData, info);
            const { aree, contenuti } = flattenForBackend(newTreeData);

            const contenutoSenzaArea = contenuti.find((c) => !c.id_area || c.id_area <= 0);
            if (contenutoSenzaArea) {
                alertService.warn(
                    'Un contenuto non può stare al di fuori di un\'area.',
                    { autoClose: true }
                );
                return;
            }

            if (typeof onReorder === 'function') {
                await onReorder({ id_corso: idCorso, aree, contenuti });
            }
        } catch (err) {
            console.error('Errore durante onDrop tree:', err);
            alertService.warn('Errore durante il riordino. Riprovare.', { autoClose: true });
        }
    };

    if (!treeData?.length) {
        return null;
    }

    return (
        <div className="custom-tree-container">
            <Tree
                treeData={treeData}
                draggable
                allowDrop={allowDrop}
                onDrop={onDrop}
                onSelect={onSelect}
                onExpand={onExpand}
                expandedKeys={expandedKeys}
                selectedKeys={selectedKeys}
                selectable
                showIcon={false}
                showLine={false}
                blockNode
                motion={null}
            />
        </div>
    );
};

export default CustomTree;
